/**
 * WallInspector — V14.76 (inspiration Homebyme).
 *
 * Inspecteur du mur sélectionné : épaisseur (cm si calibré, sinon px plan)
 * + toggle « Soupente » (hauteur réduite pour rampants de mansarde,
 * stocké dans height_override_m — déjà consommé par la 3D et le prompt).
 */
import { Ruler, TrendingDown, Fence } from 'lucide-react';
import { usePlanEditor, wallStyleFor } from './PlanEditorContext';
import { useImageDims } from './useImageDims';
import { LOW_WALL_HEIGHTS, LOW_WALL_HEIGHT_M, RAILING_STYLES, BAR_DEPTH_M } from '../../3d/geomUtils';

const DEFAULT_SOUPENTE_M = 1.2;

const LowWallControls = ({ wall, onChange }) => {
  const h = Number.isFinite(+wall.low_height_m) && +wall.low_height_m > 0 ? +wall.low_height_m : LOW_WALL_HEIGHT_M;
  const style = wall.railing_style || 'vitre';
  return (
    <div className="mt-2 pt-2 border-t border-white/[0.06] space-y-1.5" data-testid="wall-lowwall-controls">
      <div className="flex items-center gap-2">
        <Fence className="w-3 h-3 text-gray-400 shrink-0" />
        <label className="text-[10.5px] text-gray-300 flex-1">Hauteur muret</label>
        <div className="flex gap-1" role="radiogroup">
          {LOW_WALL_HEIGHTS.map((v) => (
            <button
              key={v} type="button" role="radio" aria-checked={Math.abs(h - v) < 0.01}
              onClick={() => onChange({ low_height_m: v })}
              data-testid={`wall-lowwall-height-${String(v).replace('.', '')}`}
              className="h-6 px-1.5 rounded text-[10.5px] tabular-nums transition-colors"
              style={Math.abs(h - v) < 0.01
                ? { background: 'rgba(168,85,247,0.25)', color: '#E9D5FF', border: '1px solid rgba(168,85,247,0.6)' }
                : { background: 'rgba(255,255,255,0.05)', color: '#9CA3AF', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              {v.toFixed(2).replace('.', ',')} m
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-3 shrink-0" />
        <label className="text-[10.5px] text-gray-300 flex-1">Garde-corps</label>
        <select
          value={style}
          onChange={(e) => onChange({ railing_style: e.target.value })}
          className="h-6 text-[10.5px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white focus:outline-none focus:border-amber-400/60"
          data-testid="wall-lowwall-railing"
        >
          {RAILING_STYLES.map((s) => <option key={s.value} value={s.value} className="bg-[#0F1115]">{s.label}</option>)}
        </select>
      </div>
      {style === 'comptoir' && (
        <div className="flex items-center gap-2" data-testid="wall-bar-depth-row">
          <span className="w-3 shrink-0" />
          <label className="text-[10.5px] text-gray-300 flex-1">Profondeur du comptoir</label>
          <input
            type="number" min={30} max={150} step={5}
            value={Math.round((Number.isFinite(+wall.bar_depth_m) && +wall.bar_depth_m > 0 ? +wall.bar_depth_m : BAR_DEPTH_M) * 100)}
            onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v) && v >= 20) onChange({ bar_depth_m: +(v / 100).toFixed(2) }); }}
            className="w-14 h-6 text-[11px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white text-right focus:outline-none focus:border-amber-400/60"
            data-testid="wall-bar-depth-cm"
          />
          <span className="text-[10px] text-gray-400">cm</span>
        </div>
      )}
      <p className="text-[9.5px] text-gray-500 pl-5">
        {style === 'comptoir'
          ? 'Bloc plein + plateau dans la maquette blanche et la 3D · l\'IA le texture en plan de travail (bar cuisine ↔ séjour)'
          : 'Gris moyen dans le masque IA · garde-corps rendu en 3D'}
      </p>
    </div>
  );
};

export const WallInspector = ({ bgImageUrl }) => {
  const { walls, selectedWallId, actions, calibration } = usePlanEditor();
  const dims = useImageDims(bgImageUrl);
  const wall = walls.find((w) => w.id === selectedWallId);
  if (!wall) return null;

  const mPerPx = (calibration?.scale_m_per_norm_unit && dims)
    ? calibration.scale_m_per_norm_unit / ((dims.w + dims.h) / 2)
    : null;
  const thicknessPx = +wall.thickness || 8;
  const style = wallStyleFor(wall.type);
  const hasSoupente = Number.isFinite(+wall.height_override_m) && +wall.height_override_m > 0;

  const setThicknessCm = (cm) => {
    const v = parseFloat(cm);
    if (!Number.isFinite(v) || v <= 0 || !mPerPx) return;
    actions.updateWall(wall.id, {
      thickness: Math.max(0.5, +((v / 100) / mPerPx).toFixed(2)),
      thickness_custom: true,
    });
  };
  const setThicknessPx = (px) => {
    const v = parseFloat(px);
    if (!Number.isFinite(v) || v <= 0) return;
    actions.updateWall(wall.id, { thickness: Math.max(0.5, v), thickness_custom: true });
  };

  return (
    <div className="px-2.5 py-2 border-b" style={{ borderColor: 'var(--c-border)' }}
         data-testid="wall-inspector">
      <div className="flex items-center gap-1.5 text-[10.5px] font-semibold mb-1.5"
           style={{ color: style.color }}>
        <span className="w-2.5 h-1 rounded-sm" style={{ background: style.color }} />
        Mur sélectionné · {style.label}
      </div>

      {/* V14.96 — Mur invisible : aucune épaisseur / hauteur, juste une limite virtuelle */}
      {wall.type === 'mur_invisible' ? (
        <p className="text-[10.5px] text-gray-400 leading-snug" data-testid="wall-virtual-hint">
          Limite virtuelle : découpe la pièce ouverte (ex. cuisine ↔ séjour) pour la détection et le programme,
          mais <strong className="text-gray-200">aucun mur</strong> n'est rendu (ni masque IA, ni 3D). Aucune menuiserie possible.
        </p>
      ) : (<>

      {/* Épaisseur */}
      <div className="flex items-center gap-2">
        <Ruler className="w-3 h-3 text-gray-400 shrink-0" />
        <label className="text-[10.5px] text-gray-300 flex-1">Épaisseur</label>
        {mPerPx ? (
          <div className="flex items-center gap-1">
            <input
              type="number" min={1} max={100} step={1}
              value={Math.round(thicknessPx * mPerPx * 100)}
              onChange={(e) => setThicknessCm(e.target.value)}
              className="w-14 h-6 text-[11px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white text-right focus:outline-none focus:border-amber-400/60"
              data-testid="wall-thickness-cm"
            />
            <span className="text-[10px] text-gray-400">cm</span>
          </div>
        ) : (
          <div className="flex items-center gap-1" title="Calibrez le plan (2a · Contours) pour éditer en cm">
            <input
              type="number" min={1} max={60} step={1}
              value={Math.round(thicknessPx)}
              onChange={(e) => setThicknessPx(e.target.value)}
              className="w-14 h-6 text-[11px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white text-right focus:outline-none focus:border-amber-400/60"
              data-testid="wall-thickness-px"
            />
            <span className="text-[10px] text-gray-400">px plan</span>
          </div>
        )}
      </div>

      {/* Soupente */}
      <div className="mt-1.5 flex items-center gap-2">
        <TrendingDown className="w-3 h-3 text-gray-400 shrink-0" />
        <label className="text-[10.5px] text-gray-300 flex-1"
               title="Mur bas de rampant (mansarde) — hauteur réduite prise en compte dans la 3D et le prompt IA">
          Soupente
        </label>
        <input
          type="checkbox"
          checked={hasSoupente}
          onChange={(e) => actions.updateWall(wall.id, {
            height_override_m: e.target.checked ? DEFAULT_SOUPENTE_M : null,
          })}
          className="w-3.5 h-3.5 accent-amber-500"
          data-testid="wall-soupente-toggle"
        />
      </div>
      {hasSoupente && (
        <div className="mt-1 flex items-center gap-2 pl-5">
          <label className="text-[10px] text-gray-400 flex-1">Hauteur côté bas</label>
          <input
            type="number" min={0.4} max={2.4} step={0.1}
            value={+wall.height_override_m}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (Number.isFinite(v) && v > 0) actions.updateWall(wall.id, { height_override_m: v });
            }}
            className="w-14 h-6 text-[11px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white text-right focus:outline-none focus:border-amber-400/60"
            data-testid="wall-soupente-height"
          />
          <span className="text-[10px] text-gray-400">m</span>
        </div>
      )}

      {/* V15.14 — Coupe de ce mur dans la maquette blanche */}
      {wall.type !== 'cloison_basse' && wall.type !== 'mur_invisible' && (
        <div className="mt-2 pt-2 border-t border-white/10" data-testid="wall-cut-mode">
          <div className="text-[10px] text-gray-400 mb-1">Maquette blanche · coupe de ce mur</div>
          <div className="grid grid-cols-3 gap-1">
            {[
              { id: 'auto', label: 'Auto', hint: 'Suit le plan de coupe et l\u2019option « façades du fond »' },
              { id: 'full', label: 'Toute hauteur', hint: 'Toujours à la HSP, quelle que soit la coupe' },
              { id: 'cut',  label: 'Coupé', hint: 'Toujours coupé au plan de coupe' },
            ].map((o) => {
              const cur = wall.cut_mode || 'auto';
              return (
                <button key={o.id} type="button" title={o.hint}
                        onClick={() => actions.updateWall(wall.id, { cut_mode: o.id === 'auto' ? null : o.id })}
                        className="h-6 text-[10px] rounded border transition-colors"
                        style={cur === o.id
                          ? { background: 'rgba(232,241,248,0.18)', borderColor: '#C9D9E8', color: '#E8F1F8' }
                          : { borderColor: 'rgba(255,255,255,0.12)', color: '#9CA3AF' }}
                        aria-pressed={cur === o.id}
                        data-testid={`wall-cut-mode-${o.id}`}>
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* V14.92 — Cloison basse : hauteur du muret + style de garde-corps */}
      {wall.type === 'cloison_basse' && (
        <LowWallControls wall={wall} onChange={(patch) => actions.updateWall(wall.id, patch)} />
      )}
      </>)}
    </div>
  );
};
